import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Alert, Button, InputGroup, Form, Spinner, Row, Col, PageHeader, SegmentedControl } from '@/components/ui';
import { useSearchParams } from 'react-router-dom';
import { workIdService } from '../../../services/workIdService';
import { taskService as quantifiedTaskService } from '../../../services/quantifiedTaskService';
import type { WorkIdDetail } from '../../../services/workIdService';
import { workIdService as workIdFeatureService } from '../workIdService';
import type { WorkIdRecord, WorkIdFilterOptions, WorkIdListFilters } from '../workIdService';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import WorkIdsTable from '../components/WorkIdsTable';
import WorkIdsMapView from '../components/WorkIdsMapView';
import WorkIdFilters from '../components/WorkIdFilters';
import WorkIdApproveModal from '../components/WorkIdApproveModal';
import WorkIdCancelModal from '../components/WorkIdCancelModal';
import WorkIdCloseWithoutActionModal from '../components/WorkIdCloseWithoutActionModal';
import WorkIdReopenModal from '../components/WorkIdReopenModal';
import WorkIdHistoryModal from '../components/WorkIdHistoryModal';
import WorkIdTransitionToTaskModal from '../components/WorkIdTransitionToTaskModal';
import WorkIdMergeModal from '../components/WorkIdMergeModal';
import WorkIdMergeSuggestionsModal from '../components/WorkIdMergeSuggestionsModal';
import WorkIdTaskReopenModal from '../components/WorkIdTaskReopenModal';
import WorkIdDetailModal from '../components/WorkIdDetailModal';
import WorkIdUnmergeModal from '../components/WorkIdUnmergeModal';
import WorkIdUnassignModal from '../components/WorkIdUnassignModal';
import WorkIdCreateModal from '../components/WorkIdCreateModal';
import WorkIdCloseTaskModal from '../components/WorkIdCloseTaskModal';
import CompletionModal from '../../quantified-tracking/components/taskModals/CompletionModal';

type ViewMode = 'list' | 'map';

const WorkIdsPage: React.FC = () => {
  const { loading, currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const canRegenerateSuggestions =
    hasPermission('workId:regenerate-merge-suggestions') ||
    (currentUser?.role?.name?.toLowerCase() === 'admin');
  const canCreateWorkId = hasPermission('workId:create');
  const [searchParams] = useSearchParams();
  const initialFiltersFromUrl = useMemo<WorkIdListFilters>(
    () => ({
      serviceAreaId: searchParams.get('serviceAreaId') ?? '',
      foremanAreaId: searchParams.get('foremanAreaId') ?? '',
      assetType: searchParams.get('assetType') ?? '',
      status: searchParams.get('status') ?? 'exclude-cancelled',
      search: '',
      priority: searchParams.get('priority') ?? '',
      dateFrom: '',
      dateTo: '',
      dateFilterType: 'reportedOn',
      reportedById: searchParams.get('reportedById') ?? '',
      workSource: searchParams.get('workSource') ?? ''
    }),
    [searchParams]
  );
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [filters, setFilters] = useState<WorkIdListFilters>(initialFiltersFromUrl);
  const [filterOptions, setFilterOptions] = useState<WorkIdFilterOptions>({
    serviceAreas: [],
    foremanAreas: [],
    reporters: [],
    sources: []
  });
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateMessage, setRegenerateMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [approveModalDetail, setApproveModalDetail] = useState<WorkIdDetail | null>(null);
  const [cancelModalDetail, setCancelModalDetail] = useState<WorkIdDetail | null>(null);
  const [closeWithoutActionModalDetail, setCloseWithoutActionModalDetail] = useState<WorkIdDetail | null>(null);
  const [reopenModalDetail, setReopenModalDetail] = useState<WorkIdDetail | null>(null);
  const [historyModalDetail, setHistoryModalDetail] = useState<WorkIdDetail | null>(null);
  const [assignModalRecord, setAssignModalRecord] = useState<WorkIdRecord | null>(null);
  const [assignModalInitialStep, setAssignModalInitialStep] = useState<'assign-existing' | 'create-select-type'>('assign-existing');
  const [mergeModalRecord, setMergeModalRecord] = useState<WorkIdRecord | null>(null);
  const [mergeSuggestionsModalRecord, setMergeSuggestionsModalRecord] = useState<WorkIdRecord | null>(null);
  const [taskReopenDetail, setTaskReopenDetail] = useState<WorkIdDetail | null>(null);
  const [detailModalWorkId, setDetailModalWorkId] = useState<WorkIdDetail | null>(null);
  const [detailStartInEditMode, setDetailStartInEditMode] = useState(false);
  const [unmergeModalDetail, setUnmergeModalDetail] = useState<WorkIdDetail | null>(null);
  const [unassignModalDetail, setUnassignModalDetail] = useState<WorkIdDetail | null>(null);
  const [closeTaskDetail, setCloseTaskDetail] = useState<WorkIdDetail | null>(null);
  const [reportAccomplishmentTask, setReportAccomplishmentTask] = useState<import('../../../types/Task').Task | null>(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [clearFiltersTrigger, setClearFiltersTrigger] = useState(0);
  const hasActiveFilters =
    filters.serviceAreaId !== '' ||
    filters.foremanAreaId !== '' ||
    filters.assetType !== '' ||
    (filters.status !== '' && filters.status !== 'exclude-cancelled') ||
    (filters.priority != null && filters.priority !== '') ||
    (filters.dateFrom ?? '') !== '' ||
    (filters.dateTo ?? '') !== '' ||
    (filters.reportedById ?? '') !== '' ||
    (filters.workSource ?? '') !== '' ||
    (appliedSearch ?? filters.search ?? '') !== '';

  useEffect(() => {
    const searchFromUrl = searchParams.get('search');
    if (searchFromUrl != null && searchFromUrl.trim() !== '') {
      setSearchInput(searchFromUrl.trim());
      setAppliedSearch(searchFromUrl.trim());
      setIsSearchVisible(true);
    }
  }, [searchParams]);

  const handleModalSuccess = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
    setFetchError(null);
  }, []);

  const toggleSearch = () => {
    setIsSearchVisible((prev) => !prev);
    if (isSearchVisible) {
      setSearchInput('');
      setAppliedSearch('');
    }
  };

  const handleSearch = () => {
    setAppliedSearch(searchInput.trim());
  };

  const clearSearch = () => {
    setSearchInput('');
    setAppliedSearch('');
  };

  const handleClearAllFilters = useCallback(() => {
    setSearchInput('');
    setAppliedSearch('');
    setIsSearchVisible(false);
    setFilters({
      serviceAreaId: '',
      foremanAreaId: '',
      assetType: '',
      status: 'exclude-cancelled',
      search: '',
      priority: '',
      dateFrom: '',
      dateTo: '',
      dateFilterType: 'reportedOn',
      reportedById: '',
      workSource: ''
    });
    setClearFiltersTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    setFilters(initialFiltersFromUrl);
  }, [initialFiltersFromUrl]);

  useEffect(() => {
    workIdFeatureService
      .getFilterOptions()
      .then((options) =>
        setFilterOptions({
          ...options,
          reporters: options.reporters ?? [],
          sources: options.sources ?? []
        })
      )
      .catch(() => {});
  }, []);

  const fetchAndOpen = useCallback(
    async (
      record: WorkIdRecord,
      setter: (detail: WorkIdDetail | null) => void
    ) => {
      setFetchError(null);
      setDetailLoading(true);
      try {
        const detail = await workIdService.getById(record.id);
        setter(detail);
      } catch (err) {
        setFetchError(err instanceof Error ? err.message : 'Failed to load work ID details.');
      } finally {
        setDetailLoading(false);
      }
    },
    []
  );

  const handleApprove = useCallback(
    (record: WorkIdRecord) => {
      fetchAndOpen(record, setApproveModalDetail);
    },
    [fetchAndOpen]
  );

  const handleCancel = useCallback(
    (record: WorkIdRecord) => {
      fetchAndOpen(record, setCancelModalDetail);
    },
    [fetchAndOpen]
  );

  const handleCloseWithoutAction = useCallback(
    (record: WorkIdRecord) => {
      fetchAndOpen(record, setCloseWithoutActionModalDetail);
    },
    [fetchAndOpen]
  );

  const handleReopen = useCallback(
    (record: WorkIdRecord) => {
      fetchAndOpen(record, setReopenModalDetail);
    },
    [fetchAndOpen]
  );

  const handleReviewHistory = useCallback(
    (record: WorkIdRecord) => {
      fetchAndOpen(record, setHistoryModalDetail);
    },
    [fetchAndOpen]
  );

  const handleAssignExisting = useCallback((record: WorkIdRecord) => {
    setAssignModalRecord(record);
    setAssignModalInitialStep('assign-existing');
    setFetchError(null);
  }, []);

  const handleCreateNewTask = useCallback((record: WorkIdRecord) => {
    setAssignModalRecord(record);
    setAssignModalInitialStep('create-select-type');
    setFetchError(null);
  }, []);

  const handleMerge = useCallback((record: WorkIdRecord) => {
    setMergeModalRecord(record);
    setFetchError(null);
  }, []);

  const handleViewMergeSuggestions = useCallback((record: WorkIdRecord) => {
    setMergeSuggestionsModalRecord(record);
    setFetchError(null);
  }, []);

  /**
   * Open detail modal in view-only mode (e.g., row click).
   */
  const handleOpenDetail = useCallback(
    (record: WorkIdRecord) => {
      setDetailStartInEditMode(false);
      fetchAndOpen(record, setDetailModalWorkId);
    },
    [fetchAndOpen]
  );

  /**
   * Open detail modal directly in edit mode (action-bar Edit).
   */
  const handleEdit = useCallback(
    (record: WorkIdRecord) => {
      setDetailStartInEditMode(true);
      fetchAndOpen(record, setDetailModalWorkId);
    },
    [fetchAndOpen]
  );

  const handleOpenMergedParent = useCallback(
    (record: WorkIdRecord) => {
      const parentId = record.mergedIntoParent?.id;
      if (parentId == null) {
        setFetchError('Parent work ID not found for this merged record.');
        return;
      }
      setDetailStartInEditMode(false);
      fetchAndOpen({ id: parentId } as WorkIdRecord, setDetailModalWorkId);
    },
    [fetchAndOpen]
  );

  const handleReopenTask = useCallback(
    (record: WorkIdRecord) => {
      fetchAndOpen(record, setTaskReopenDetail);
    },
    [fetchAndOpen]
  );

  const handleUnmerge = useCallback(
    (record: WorkIdRecord) => {
      fetchAndOpen(record, setUnmergeModalDetail);
    },
    [fetchAndOpen]
  );

  const handleUnassign = useCallback(
    (record: WorkIdRecord) => {
      fetchAndOpen(record, setUnassignModalDetail);
    },
    [fetchAndOpen]
  );

  const handleCloseTask = useCallback(
    (record: WorkIdRecord) => {
      fetchAndOpen(record, setCloseTaskDetail);
    },
    [fetchAndOpen]
  );

  const handleReportAccomplishment = useCallback(
    async (record: WorkIdRecord) => {
      const task = record.tasks?.[0];
      if (!task || task.type !== 'Quantified') return;
      setFetchError(null);
      setDetailLoading(true);
      try {
        const fullTask = await quantifiedTaskService.getTask(task.id);
        setReportAccomplishmentTask(fullTask);
      } catch (err) {
        setFetchError(err instanceof Error ? err.message : 'Failed to load task details.');
      } finally {
        setDetailLoading(false);
      }
    },
    []
  );

  const closeApproveModal = () => {
    setApproveModalDetail(null);
    setFetchError(null);
  };

  const closeCancelModal = () => {
    setCancelModalDetail(null);
    setFetchError(null);
  };

  const closeCloseWithoutActionModal = () => {
    setCloseWithoutActionModalDetail(null);
    setFetchError(null);
  };

  const closeReopenModal = () => {
    setReopenModalDetail(null);
    setFetchError(null);
  };

  const closeHistoryModal = () => {
    setHistoryModalDetail(null);
    setFetchError(null);
  };

  const closeAssignModal = () => {
    setAssignModalRecord(null);
    setFetchError(null);
  };

  const closeMergeModal = () => {
    setMergeModalRecord(null);
    setFetchError(null);
  };

  const closeMergeSuggestionsModal = () => {
    setMergeSuggestionsModalRecord(null);
    setFetchError(null);
  };

  const closeTaskReopenModal = () => {
    setTaskReopenDetail(null);
    setFetchError(null);
  };

  const closeDetailModal = () => {
    setDetailModalWorkId(null);
    setFetchError(null);
    setDetailStartInEditMode(false);
  };

  const closeUnmergeModal = () => {
    setUnmergeModalDetail(null);
    setFetchError(null);
  };

  const closeUnassignModal = () => {
    setUnassignModalDetail(null);
    setFetchError(null);
  };

  const closeCloseTaskModal = () => {
    setCloseTaskDetail(null);
    setFetchError(null);
  };

  const closeReportAccomplishmentModal = () => {
    setReportAccomplishmentTask(null);
    setFetchError(null);
  };

  const handleRegenerateMergeSuggestions = useCallback(async () => {
    setRegenerateMessage(null);
    setRegenerating(true);
    try {
      const result = await workIdService.regenerateAllMergeSuggestions();
      setRegenerateMessage({
        type: 'success',
        text: `Regenerated merge suggestions for ${result.processed} of ${result.total} Work IDs.${result.errors?.length ? ` ${result.errors.length} error(s).` : ''}`,
      });
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      setRegenerateMessage({
        type: 'danger',
        text: err instanceof Error ? err.message : 'Failed to regenerate merge suggestions.',
      });
    } finally {
      setRegenerating(false);
    }
  }, []);

  if (loading) {
    return <div className="tw-w-full tw-p-4">Loading...</div>;
  }

  return (
    <div className="work-ids-page tw-w-full tw-p-4 tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
      <PageHeader
        title="Work ID Management"
        actions={[
          {
            key: 'refresh',
            label: 'Refresh',
            // Was `variant="success"` (green) — Refresh is chrome, not a success outcome.
            icon: 'bi-arrow-clockwise',
            intent: 'neutral',
            onClick: () => setRefreshKey((prev) => prev + 1),
          },
          {
            key: 'regenerate',
            label: regenerating ? 'Regenerating…' : 'Regenerate merge suggestions',
            icon: regenerating ? 'bi-hourglass-split' : 'bi-diagram-3',
            intent: 'secondary',
            hidden: !canRegenerateSuggestions,
            disabled: regenerating,
            title: 'Regenerate merge suggestions for all eligible Work IDs (admin only)',
            onClick: handleRegenerateMergeSuggestions,
          },
          {
            key: 'search',
            label: `Search${appliedSearch ? ` (${appliedSearch.length > 20 ? appliedSearch.substring(0, 20) + '…' : appliedSearch})` : ''}`,
            icon: isSearchVisible ? 'bi-x-lg' : 'bi-search',
            intent: 'secondary',
            active: isSearchVisible || !!appliedSearch,
            title: isSearchVisible ? 'Close search' : 'Search Work IDs',
            onClick: toggleSearch,
          },
          {
            key: 'clear',
            label: 'Clear All',
            icon: 'bi-x-circle',
            intent: 'neutral',
            hidden: !(hasActiveFilters || isSearchVisible || appliedSearch),
            title: 'Clear all filters and search',
            onClick: handleClearAllFilters,
          },
          {
            key: 'create',
            label: 'Create Work ID',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !canCreateWorkId,
            title: 'Create a new Work ID',
            onClick: () => setCreateModalVisible(true),
          },
        ]}
      >
        <SegmentedControl
          aria-label="Work ID display"
          value={viewMode}
          onChange={setViewMode}
          options={[
            { value: 'list', label: 'List', icon: 'bi-list-ul' },
            { value: 'map', label: 'Map', icon: 'bi-map' },
          ]}
        />
      </PageHeader>

      <div className="tw-shrink-0">
        <WorkIdFilters
          filterOptions={filterOptions}
          filters={filters}
          onClearFilters={handleClearAllFilters}
          onApplyFilters={(newFilters) =>
            setFilters((prev) => ({ ...prev, ...newFilters, search: prev.search ?? '' }))
          }
        />
      </div>

      {isSearchVisible && (
        <Row className="tw-mb-3 tw-shrink-0">
          <Col xs={12}>
            <InputGroup className="tw-shadow-sm">
              <InputGroup.Text className="tw-bg-primary tw-text-white tw-border-primary">
                <i className="bi bi-search"></i>
              </InputGroup.Text>
              <Form.Control
                type="text"
                size="sm"
                placeholder="Search by Work ID, comments, asset, reporter..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
                autoFocus
              />
              <Button
                variant="primary"
                size="sm"
                type="button"
                onClick={handleSearch}
                title="Search"
              >
                <i className="bi bi-search"></i>
              </Button>
              {(searchInput || appliedSearch) && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  type="button"
                  onClick={clearSearch}
                  title="Clear search"
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              )}
            </InputGroup>
            {appliedSearch && (
              <div className="tw-text-content-muted tw-text-sm tw-mt-1">
                Searching for: &quot;{appliedSearch}&quot;
              </div>
            )}
          </Col>
        </Row>
      )}

      {fetchError && (
        <Alert variant="danger" dismissible onClose={() => setFetchError(null)} className="tw-shrink-0">
          {fetchError}
        </Alert>
      )}

      {regenerateMessage && (
        <Alert variant={regenerateMessage.type} dismissible onClose={() => setRegenerateMessage(null)} className="tw-shrink-0">
          {regenerateMessage.text}
        </Alert>
      )}

      {detailLoading && (
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2 tw-text-content-muted tw-text-sm">
          <Spinner as="span" size="sm" aria-hidden="true" />
          Loading work ID details…
        </div>
      )}

      {viewMode === 'list' ? (
        <div className="tw-grow tw-flex tw-flex-col tw-min-h-0" style={{ minHeight: 0 }}>
        <WorkIdsTable
          key={refreshKey}
          refreshKey={refreshKey}
          filters={filters}
          onFiltersChange={(newFilters) =>
            setFilters((prev) => ({
              ...prev,
              ...newFilters,
              search: newFilters.search ?? prev.search ?? ''
            }))
          }
          filterOptions={filterOptions}
          search={appliedSearch}
          onSearchCleared={() => {
            setSearchInput('');
            setAppliedSearch('');
          }}
          clearFiltersTrigger={clearFiltersTrigger}
          hideFilters
          onApprove={handleApprove}
          onCancel={handleCancel}
          onCloseWithoutAction={handleCloseWithoutAction}
          onReopen={handleReopen}
          onAssignExisting={handleAssignExisting}
          onCreateNewTask={handleCreateNewTask}
          onUnassign={handleUnassign}
          onMerge={handleMerge}
          onViewMergeSuggestions={handleViewMergeSuggestions}
          onUnmerge={handleUnmerge}
          onReviewHistory={handleReviewHistory}
          onOpenDetail={handleOpenDetail}
          onEdit={handleEdit}
          onReopenTask={handleReopenTask}
          onReportAccomplishment={handleReportAccomplishment}
          onCloseTask={handleCloseTask}
          onOpenMergedParent={handleOpenMergedParent}
        />
        </div>
      ) : (
        <div className="tw-grow tw-flex tw-flex-col tw-min-h-0" style={{ minHeight: 0 }}>
          <WorkIdsMapView
            key={refreshKey}
            refreshKey={refreshKey}
            filters={filters}
            search={appliedSearch}
            filterOptions={filterOptions}
            onApprove={handleApprove}
            onCancel={handleCancel}
            onCloseWithoutAction={handleCloseWithoutAction}
            onReopen={handleReopen}
            onAssignExisting={handleAssignExisting}
            onCreateNewTask={handleCreateNewTask}
            onUnassign={handleUnassign}
            onMerge={handleMerge}
            onViewMergeSuggestions={handleViewMergeSuggestions}
            onUnmerge={handleUnmerge}
            onReviewHistory={handleReviewHistory}
            onEdit={handleEdit}
            onReopenTask={handleReopenTask}
            onReportAccomplishment={handleReportAccomplishment}
            onCloseTask={handleCloseTask}
          />
        </div>
      )}

      <WorkIdApproveModal
        visible={!!approveModalDetail}
        onClose={closeApproveModal}
        workId={approveModalDetail}
        onSuccess={() => {
          handleModalSuccess();
          closeApproveModal();
        }}
      />

      <WorkIdCancelModal
        visible={!!cancelModalDetail}
        onClose={closeCancelModal}
        workId={cancelModalDetail}
        onSuccess={() => {
          handleModalSuccess();
          closeCancelModal();
        }}
      />

      <WorkIdCloseWithoutActionModal
        visible={!!closeWithoutActionModalDetail}
        onClose={closeCloseWithoutActionModal}
        workId={closeWithoutActionModalDetail}
        onSuccess={() => {
          handleModalSuccess();
          closeCloseWithoutActionModal();
        }}
      />

      <WorkIdReopenModal
        visible={!!reopenModalDetail}
        onClose={closeReopenModal}
        workId={reopenModalDetail}
        onSuccess={() => {
          handleModalSuccess();
          closeReopenModal();
        }}
      />

      <WorkIdHistoryModal
        visible={!!historyModalDetail}
        onClose={closeHistoryModal}
        workId={historyModalDetail}
      />

      <WorkIdTransitionToTaskModal
        visible={!!assignModalRecord}
        onClose={closeAssignModal}
        workId={assignModalRecord}
        initialStep={assignModalInitialStep}
        onSuccess={() => {
          handleModalSuccess();
          closeAssignModal();
        }}
      />

      <WorkIdMergeModal
        visible={!!mergeModalRecord}
        onClose={closeMergeModal}
        workId={mergeModalRecord}
        onSuccess={() => {
          handleModalSuccess();
          closeMergeModal();
        }}
      />

      <WorkIdMergeSuggestionsModal
        visible={!!mergeSuggestionsModalRecord}
        onClose={closeMergeSuggestionsModal}
        workId={mergeSuggestionsModalRecord}
        onSuccess={() => {
          handleModalSuccess();
          closeMergeSuggestionsModal();
        }}
      />

      <WorkIdTaskReopenModal
        visible={!!taskReopenDetail}
        onClose={closeTaskReopenModal}
        workId={taskReopenDetail}
        onSuccess={() => {
          handleModalSuccess();
          closeTaskReopenModal();
        }}
      />

      <WorkIdDetailModal
        visible={!!detailModalWorkId}
        onClose={closeDetailModal}
        workId={detailModalWorkId}
        startInEditMode={detailStartInEditMode}
        onSuccess={() => {
          handleModalSuccess();
        }}
      />

      <WorkIdUnmergeModal
        visible={!!unmergeModalDetail}
        onClose={closeUnmergeModal}
        workId={unmergeModalDetail}
        onSuccess={() => {
          handleModalSuccess();
          closeUnmergeModal();
        }}
      />

      <WorkIdUnassignModal
        visible={!!unassignModalDetail}
        onClose={closeUnassignModal}
        workId={unassignModalDetail}
        onSuccess={() => {
          handleModalSuccess();
          closeUnassignModal();
        }}
      />

      <WorkIdCreateModal
        visible={createModalVisible}
        onClose={() => setCreateModalVisible(false)}
        onSuccess={() => {
          handleModalSuccess();
          setCreateModalVisible(false);
        }}
      />

      <WorkIdCloseTaskModal
        visible={!!closeTaskDetail}
        onClose={closeCloseTaskModal}
        workId={closeTaskDetail}
        onSuccess={() => {
          handleModalSuccess();
          closeCloseTaskModal();
        }}
      />

      {reportAccomplishmentTask && (
        <CompletionModal
          visible={!!reportAccomplishmentTask}
          onClose={closeReportAccomplishmentModal}
          task={reportAccomplishmentTask}
          onComplete={() => {
            handleModalSuccess();
            closeReportAccomplishmentModal();
          }}
        />
      )}
    </div>
  );
};

export default WorkIdsPage;
